import { useEffect } from 'react';
import { datadog } from '@sg/expo-datadog-client';
import { useFeatureFlagFactory } from '@sg/launch-darkly';

import { type FeatureFlags } from '../types';
import { LaunchDarklyProviderContext } from './LaunchDarklyProviderContext';

// ─────────────────────────────────────────────────────────────────────────────

export const useFeatureFlag = <Key extends keyof FeatureFlags>(
  key: Key,
  _options?: Options,
) => {
  const featureFlagValue = useFeatureFlagFactory<FeatureFlags, Key>(
    key,
    FACTORY_OPTIONS,
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    void datadog.addFeatureFlagEvaluation(key, featureFlagValue);
  }, [featureFlagValue, key]);

  // ─────────────────────────────────────────────────────────────────────

  return featureFlagValue;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const FACTORY_OPTIONS: FactoryOptions = {
  Context: LaunchDarklyProviderContext,
  featureFlagOverrideKey: 'permanent-feature-flag-overrides-enabled',
  onChange: datadog.addFeatureFlagEvaluation,
};

// ─── Types ───────────────────────────────────────────────────────────── ──────

type Options = {
  /**
   * @deprecated
   *
   * Now all flags are automatically listened for changes.
   */
  listenForChanges?: boolean;
};

type FactoryOptions = Parameters<
  typeof useFeatureFlagFactory<FeatureFlags, keyof FeatureFlags>
>[1];
